import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Box, 
    InputBase, 
    IconButton, 
    Tabs, 
    Tab,
    Grid
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import triangleLogo from "../assets/images/triangle.png";

const LogoSearchHeader = () => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [searchTab, setSearchTab] = useState(0); // 0: AI Search, 1: Build your Search, 2: Persona Search
    
    // Typewriter animation for placeholder text
    const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
    const [displayedText, setDisplayedText] = useState("");
    const [isTyping, setIsTyping] = useState(true);
    
    const placeholderTexts = [
        "Search with AI...",
        "Find software engineers",
        "Search for product managers",
        "Find marketing experts",
        "Search for graphics designers",
        "Find sales professionals",
        "principal engineer working for internet companies",
        "Vice President, Sales, Walmart",
        "Head of Product, Google"
    ];
    


    const handleTabChange = (event, newValue) => {
        setSearchTab(newValue);
    };

    const getSearchType = () => {
        switch (searchTab) {
            case 0:
                return "ai";
            case 1:
                return "build";
            case 2:
                return "persona";
            default:
                return "ai";
        }
    };

    const getTabLabel = (index) => {
        switch (index) {
            case 0:
                return "AI Search";
            case 1:
                return "Build your Search";
            case 2:
                return "Persona Search";
            default:
                return "";
        }
    };



    // Typewriter animation effect
    useEffect(() => {
        const currentText = placeholderTexts[currentPlaceholderIndex];
        
        if (isTyping) {
            // Typing phase
            if (displayedText.length < currentText.length) {
                const timer = setTimeout(() => {
                    setDisplayedText(currentText.slice(0, displayedText.length + 1));
                }, 100); // Type speed: 100ms per character
                return () => clearTimeout(timer);
            } else {
                // Finished typing, wait then start deleting
                const timer = setTimeout(() => {
                    setIsTyping(false);
                }, 1500); // Wait 1.5 seconds before deleting
                return () => clearTimeout(timer);
            }
        } else {
            // Deleting phase
            if (displayedText.length > 0) {
                const timer = setTimeout(() => {
                    setDisplayedText(displayedText.slice(0, -1));
                }, 50); // Delete speed: 50ms per character
                return () => clearTimeout(timer);
            } else {
                // Finished deleting, move to next text
                setIsTyping(true);
                setCurrentPlaceholderIndex((prevIndex) => 
                    (prevIndex + 1) % placeholderTexts.length
                );
            }
        }
    }, [displayedText, isTyping, currentPlaceholderIndex, placeholderTexts]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchText.trim()) {
            const params = new URLSearchParams();
            params.set("query", searchText);
            params.set("page", 1);
            params.set("type", getSearchType());
            navigate(`/search?${params.toString()}`);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center",py: 2}}>
            {/* Logo */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: "center", gap: 2}}>
                <img src={triangleLogo} alt="Triangle Logo" style={{ width: "200px", height: "200px", objectFit: "contain"}} />
            </Box>

            {/* New Taglines */}
            <Box sx={{ textAlign: "center", mb: 2}}>
                <Box component="h2" sx={{ fontSize: "2.8rem", color: "#212121", lineHeight: 1.2,  fontFamily: "Poppins"}}>
                    Find the right leaders, faster
                </Box>
                <Box component="p" sx={{ fontSize: "1.5rem", color: grey[600], fontFamily: "Poppins"}}>
                    simplifying discovery for early manager and leaders
                </Box>
            </Box>

            {/* Search Section */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2,width: "100%",maxWidth: "800px"}}>
                {/* Search Tabs */}

                {/* Search Bar */}
                <Box 
                    sx={{ 
                        bgcolor: "#fff", 
                        borderRadius: 3, 
                        px: 3, 
                        py: 1.5, 
                        width: "100%", 
                        display: "flex", 
                        alignItems: "center",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        border: "2px solid transparent",
                        "&:hover": {
                            borderColor: "#1BE885",
                        },
                        transition: "all 0.3s ease"
                    }}
                >
                    <form style={{ display: "flex", width: "100%" }} onSubmit={handleSearch} >            
                        <InputBase 
                            placeholder={displayedText || "Search with AI..."} 
                            sx={{ 
                                color: "#000", 
                                width: "100%", 
                                fontSize: "1.1rem", 
                                fontWeight: 400,
                                transition: "all 0.3s ease"
                            }} 
                            value={searchText} 
                            onChange={e => setSearchText(e.target.value)} 
                        />
                        <IconButton sx={{ color: '#1BE885', ml: 1,"&:hover": {bgcolor: "rgba(27, 232, 133, 0.1)"}}} aria-label="Search" type="submit">
                            <Search sx={{ fontSize: "1.5rem" }} />
                        </IconButton>
                    </form>
                </Box>

                {/* Search Description */}
                <Box sx={{ textAlign: "center", color: grey[600], fontSize: "0.9rem", maxWidth: "600px"}}>
                    {searchTab === 0 && "Use AI-powered search to find candidates with natural language queries"}
                    {searchTab === 1 && "Build complex search filters to find specific candidate profiles"}
                    {searchTab === 2 && "Search by persona types to find candidates with specific characteristics"}
                </Box>
                

            </Box>

            {/* Statistics/Metrics Section */}
            <Box sx={{ width: "100%", maxWidth: "800px", mt: 4 }}>
                
                <Grid container spacing={3} justifyContent="center">
                {/* Search Conducted */}
                <Grid item xs={6} sm={3}>
                    <Box sx={{textAlign: "center", p: 2, borderRadius: 2, bgcolor: "rgba(27, 232, 133, 0.05)", border: "1px solid rgba(27, 232, 133, 0.1)"}}>
                        <Box component="div" sx={{ fontSize: "2rem", fontWeight: 700, color: "#1BE885", mb: 0.5 }}>49</Box>
                        <Box component="div" sx={{ fontSize: "0.9rem", color: grey[600], fontWeight: 500 }}>Search<br />conducted</Box>
                    </Box>
                </Grid>
                {/* Tag Created */}
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, bgcolor: "rgba(27, 232, 133, 0.05)", border: "1px solid rgba(27, 232, 133, 0.1)" }}>
                        <Box component="div" sx={{ fontSize: "2rem", fontWeight: 700, color: "#1BE885", mb: 0.5 }}>20</Box>
                        <Box component="div" sx={{ fontSize: "0.9rem", color: grey[600], fontWeight: 500 }}>Tag<br />Created</Box>
                    </Box>
                </Grid>

                {/* Listed Created */}
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, bgcolor: "rgba(27, 232, 133, 0.05)", border: "1px solid rgba(27, 232, 133, 0.1)"}}>
                        <Box component="div" sx={{ fontSize: "2rem", fontWeight: 700, color: "#1BE885", mb: 0.5}}>35</Box>                                              
                        <Box component="div" sx={{ fontSize: "0.9rem", color: grey[600], fontWeight: 500}}>Listed<br />Created</Box>
                    </Box>
                </Grid>

                {/* Team Created */}
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: "center",p: 2,borderRadius: 2,bgcolor: "rgba(27, 232, 133, 0.05)",border: "1px solid rgba(27, 232, 133, 0.1)"}}>
                        <Box component="div" sx={{ fontSize: "2rem", fontWeight: 700, color: "#1BE885",mb: 0.5}}>02</Box>
                        <Box component="div" sx={{ fontSize: "0.9rem", color: grey[600],fontWeight: 500}}>Team<br />Created</Box>
                    </Box>
                </Grid>

                {/* Lead Left */}
                <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: "center",p: 2,borderRadius: 2,bgcolor: "rgba(27, 232, 133, 0.05)",border: "1px solid rgba(27, 232, 133, 0.1)"}}>
                        <Box component="div" sx={{ fontSize: "2rem", fontWeight: 700, color: "#1BE885",mb: 0.5}}>07</Box>
                        <Box component="div" sx={{ fontSize: "0.9rem", color: grey[600],fontWeight: 500}}>Lead<br />Left</Box>
                    </Box>
                </Grid>
                </Grid>
            
            </Box>
        </Box>
    );
};

export default LogoSearchHeader; 